package fyi.moko.hotpl.ui.map

import androidx.compose.ui.graphics.Color
import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.PlaceVisitSummary
import fyi.moko.hotpl.domain.model.isWantToGo

/**
 * ── 지도 핀 색은 한 가지다 (2026-09-17 사용자 결정) ──────────────────────────────
 * 1.0.16~1.0.22는 리뷰어 요청(R-8)으로 마지막 방문의 감정 8색을 핀에 얹었다. 되돌린 이유:
 *  · 지도에서 궁금한 것은 "어디에 무엇이 있나"다. 감정은 핀을 눌러 들어가면 보인다 — 8색을
 *    외워 지도 위에서 읽는 사람은 없다(R-8 주석 스스로 "범례 없이 읽기 빠듯하다"고 적었다).
 *  · 감정은 마지막 방문 하나로 대표돼 신뢰도가 낮다.
 *  · 색이 들쭉날쭉하면 오히려 **가본 곳/안 가본 곳**(속 찬 핀/속 빈 핀, R-15)의 구분이 묻힌다.
 *    한 색으로 통일하면 채움 여부가 지도에서 첫눈에 읽힌다 — iOS 시뮬레이터에서 여섯 색으로
 *    흩어진 화면(2026-09-14)이 결정의 근거다.
 * 카드의 감정 색 점은 그대로다. [PlacesMap]의 `pinColors` 입력은 남겨 두었다 — 비워 두면
 * 모두 [NEUTRAL_PIN_COLOR]. 다시 색을 얹기로 하면 그 자리에 맵을 넣으면 된다.
 */

/**
 * 모든 핀의 색. 안 가본 곳도 같은 색으로, 속만 비운다.
 *
 * ── 왜 회색이 아니라 빨강인가 (0-26, 2026-09-10) ──────────────────────────────
 * 1.0.15는 "정해지지 않았다"를 회색으로 읽히게 했는데, 실기기에서 그 회색 원이 구글 지도
 * 자체의 회색 POI 점과 구별되지 않아 리뷰어가 "뭐가 뭔지 모르겠다"고 했다. 리뷰어 결정으로
 * 기본 핀은 구글 마커의 빨강을 쓴다 — 지도 사용자라면 누구나 "내 핀"으로 읽는 색이다.
 * 그 자리를 비우려고 Lively는 노랑으로 옮겼다([fyi.moko.hotpl.ui.theme.dotColor]).
 */
val NEUTRAL_PIN_COLOR = Color(0xFFE0392B)

/**
 * 안 가본 곳의 id — 지도가 **속 빈** 물방울로 그린다 (R-15, 0-32).
 *
 * ── 왜 색이 아니라 모양인가 ──────────────────────────────
 * 리뷰어는 "Been과 Want to go를 핀 색으로 나누고 싶다"고 했지만 당시 핀 색은 감정 채널이었다
 * (R-8). 그래서 안 가본 곳은 채우지 않은 핀으로 그렸고, 색을 한 가지로 닫은 지금도 이 모양
 * 구분을 그대로 쓴다. "아직 비어 있다"가 모양
 * 자체로 읽히고, 색을 구별 못 하는 사용자에게도 이 채널은 남는다.
 */
fun wantToGoPlaceIds(places: List<Place>, visitSummaries: Map<String, PlaceVisitSummary>): Set<String> =
    places.filter { it.isWantToGo(visitSummaries[it.id]) }.mapTo(mutableSetOf()) { it.id }
